import {NgModule} from '@angular/core';
import {FuseNavigationService} from '@fuse/components/navigation/navigation.service';
import {BaseAdvisorInfoModel} from '@models/api/@base/base-advisor-info.model';
import {ProfileModel} from '@models/api/advisor/profile.model';
import {UserAppFeatureMatrix} from '@models/api/user/user-app-feature.matrix';
import {ApiAdvisorService} from '@services/api/api-advisor.service';
import {ApiUserSessionService} from '@services/api/api-user-session.service';
import {ApiUserService} from '@services/api/api-user.service';
import {NotificationsService} from 'angular2-notifications';
import {AuthService} from 'app/core/auth.service';
import {environment} from 'environments/environment';

@NgModule({})
export class UserInfoModule {
    static userAppFeatureMatrix: UserAppFeatureMatrix = {features: {}};
    static profiles: ProfileModel[] = [];

    private _selectedLang: any;

    get selectedLang(): any {
        return this._selectedLang;
    }

    set selectedLang(value: any) {
        this._selectedLang = value;
    }

    private profileAvatarImage = '';
    private userAvatarImage = '';
    primaryProfile: BaseAdvisorInfoModel = null;

    constructor(
        private authService: AuthService,
        private apiAdvisorService: ApiAdvisorService,
        private apiUserService: ApiUserService,
        private apiUserSessionService: ApiUserSessionService,
        private fuseNavigationService: FuseNavigationService,
        private notificationsService: NotificationsService,
    ) {
        this.authService.isDoneLoading$.subscribe((v) => {
            // filter main menu
            this.fuseNavigationService.updateNavigationItem('devgroup', {hidden: environment.production});
            this.fuseNavigationService.updateNavigationItem('main_group', {hidden: this.isNE});
            this.fuseNavigationService.updateNavigationItem('reports-hq', {hidden: !(this.isBO || this.isAN)});
            this.fuseNavigationService.updateNavigationItem('advisor-commision', {hidden: (this.isAA || this.isNE)});

            if (v) {
                this.downloadProfileAvatar();
                this.downloadUserAvatar();
                this.downloadFeatures();
                this.apiAdvisorService.getPrimary()
                    .subscribe({
                        next: (d) => this.primaryProfile = d
                        , error: (_) => this.primaryProfile = null
                        , complete: () => null
                    });
                this.apiAdvisorService.getProfiles()
                    .subscribe({
                        next: (d) => UserInfoModule.profiles = d
                        , error: (_) => UserInfoModule.profiles = []
                        , complete: () => null
                    });
            }
        });

        this.authService.canActivateProtectedRoutes$.subscribe(value => {
        });
    }

    public static isFeatureAvailable(code: string): boolean {
        if (UserInfoModule.userAppFeatureMatrix && UserInfoModule.userAppFeatureMatrix.features.hasOwnProperty(code)) {
            return UserInfoModule.userAppFeatureMatrix.features[code];
        }

        return false;
    }

    public get isManager(): boolean {
        const primaryProfile = UserInfoModule.profiles.find(p => p.isPrimary);
        if (primaryProfile) {
            return primaryProfile.managerPosition && primaryProfile.managerPosition.code !== null;
        }
        return false;
    }

    private downloadFeatures(): void {
        this.apiUserService.getAppFeatureMatrix()
            .subscribe((r) => {
                UserInfoModule.userAppFeatureMatrix = r;
                if (!environment.production) {
                    console.table(r.features);
                }

                this.fuseNavigationService.updateNavigationItem('reports', {
                    hidden: !(
                        UserInfoModule.isFeatureAvailable('report-advisory-r-units') ||
                        UserInfoModule.isFeatureAvailable('report-advisory-cashflow') ||
                        UserInfoModule.isFeatureAvailable('report-advisory-portfolio-complexity') ||
                        UserInfoModule.isFeatureAvailable('report-central')
                    )
                });
                this.fuseNavigationService.updateNavigationItem('reports-hq', {hidden: !(this.isBO || this.isAN) || !UserInfoModule.isFeatureAvailable('report-central')});
                this.fuseNavigationService.updateNavigationItem('manufactory', {hidden: !UserInfoModule.isFeatureAvailable('manufactory')});
            }, (e) => {
                this.notificationsService.warn('Upozornění', 'Došlo k chybě při stahování uživatelských funkcionalit. Aplikace nemusí pracovat správně.');
            });
    }

    private downloadProfileAvatar(): void {
        this.apiAdvisorService.getAvatar()
            .subscribe((res) => {
                this.profileAvatarImage = res;
            });
    }

    private downloadUserAvatar(): void {
        this.apiUserSessionService.getAvatar()
            .subscribe((res) => {
                this.userAvatarImage = res;
            });
    }

    public logoutUser(): void {
        this.authService.logout();
    }

    public get personalIdentifier(): string {
        return this.authService.identityClaims == null ? null : this.authService.identityClaims['personal_identifier'];
    }

    public get userFullName(): string {
        return this.authService.identityClaims == null ? null : this.authService.identityClaims['name'];
    }

    public get userGivenName(): string {
        return this.authService.identityClaims == null ? null : this.authService.identityClaims['given_name'];
    }

    public get userFamilyName(): string {
        return this.authService.identityClaims == null ? null : this.authService.identityClaims['family_name'];
    }

    public get userEmail(): string {
        return this.authService.identityClaims == null ? null : this.authService.identityClaims['email'];
    }

    public get userAdvisorId(): string {
        return this.authService.identityClaims == null ? null : this.authService.identityClaims['advisor_id'];
    }

    public get profileAvatar(): string {
        return this.profileAvatarImage;
    }

    public get userAvatar(): string {
        return this.userAvatarImage;
    }

    public get isSirius(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims['advisor_broker'] === 'SIRIUS';
    }

    public get isUMD(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims['advisor_broker'] === 'UMD';
    }

    public get isROOT(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims['advisor_broker'] === 'ROOT';
    }

    /**
     * poradce
     */
    public get isAR(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims['user_role'] === 'AR';
    }

    /**
     * asistentka
     */
    public get isAA(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims['user_role'] === 'AA';
    }

    /**
     * admin
     */
    public get isAN(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims['user_role'] === 'AN';
    }

    /**
     * backoffice - aktualne centrala
     */
    public get isBO(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims['user_role'] === 'BO';
    }

    /**
     * novy poradce
     */
    public get isNE(): boolean {
        return this.authService.identityClaims != null && this.authService.identityClaims['user_role'] === 'NE';
    }

}
